import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useRole } from "../context/RoleContext";
import { listCases, exportUrl } from "../lib/cityPortal";
import { Search, Download } from "lucide-react";
import { relativeDate, StagePill } from "../lib/ownerUi";
import ListTable, { ListRow } from "../components/ListTable";

const FILTERS = [
  { v: "all", label: "All" },
  { v: "in_review", label: "In Expedited Review" },
  { v: "permit_issued", label: "Permit Issued" },
  { v: "under_construction", label: "Under Construction" },
  { v: "complete", label: "Complete" },
  { v: "sync_error", label: "Sync Error" },
];

const SYNC_TONE = {
  synced: { fg: "#4F6149", bg: "rgba(79,97,73,0.10)", label: "Synced" },
  pending: { fg: "#8a651f", bg: "rgba(185,138,60,0.14)", label: "Pending sync" },
  error: { fg: "#A23B2E", bg: "rgba(162,59,46,0.10)", label: "Sync error" },
};

/* Shared column template: Case · Stage · Sync · Last sync */
const CASE_GRID = "minmax(0, 2.2fr) 160px 140px 130px";
const CASE_GAP = 16;

export default function CityCases() {
  const { user } = useRole();
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    const t = setTimeout(() => {
      listCases(user.id, { status, q }).then((d) => alive && setData(d));
    }, 200);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [user?.id, status, q]);

  return (
    <section className="animate-fade-in-up" data-testid="city-cases-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <span aria-hidden style={{ display: "inline-block", width: 28, height: 2, background: "#B14E2F" }} />
            <span className="wn-label" style={{ color: "#8F3C23" }}>Permitting · Read-only</span>
          </div>
          <h1
            className="mt-3"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontSize: "clamp(26px, 3.6vw, 32px)",
              fontWeight: 600,
              color: "#1B1F4B",
            }}
          >
            Accela Cases.
          </h1>
          <div className="mt-1 flex items-center gap-2 flex-wrap" style={{ fontSize: 13, color: "#6C6B85" }}>
            <span
              aria-hidden
              style={{ width: 8, height: 8, borderRadius: 999, background: "#4F6149", display: "inline-block" }}
            />
            <span style={{ fontFamily: '"Newsreader", Georgia, serif', fontStyle: "italic" }}>
              Connected to Accela (simulated). Decisions on permits are recorded in Accela —
              this surface is observational.
            </span>
          </div>
        </div>
        <a
          href={exportUrl("cases", user?.id, { status, q })}
          className="wn-btn wn-btn-tertiary"
          data-testid="city-cases-export"
        >
          <Download size={14} /> Export cases (CSV)
        </a>
      </div>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap" data-testid="city-cases-filters">
          {FILTERS.map((f) => {
            const active = status === f.v;
            return (
              <button
                key={f.v}
                onClick={() => setStatus(f.v)}
                data-testid={`city-cases-filter-${f.v}`}
                style={{
                  padding: "5px 12px",
                  borderRadius: 999,
                  border: "1px solid " + (active ? "#1B1F4B" : "#D9D8E4"),
                  background: active ? "#1B1F4B" : "transparent",
                  color: active ? "#FBFAFD" : "#272636",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {f.label}
                {data?.counts?.[f.v] != null && (
                  <span
                    className="tabular-nums"
                    style={{ marginLeft: 6, opacity: 0.7 }}
                  >
                    {data.counts[f.v]}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 ml-auto" style={{ minWidth: 240 }}>
          <Search size={14} style={{ color: "#6C6B85" }} />
          <input
            className="wn-input"
            placeholder="Case number, parcel, owner"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="city-cases-search"
            style={{ flex: 1 }}
          />
        </div>
      </div>

      <div style={{ borderTop: "1px solid #D9D8E4", marginTop: 16 }} />

      <ListTable
        testId="city-cases"
        columns={[
          { label: "Case · Address" },
          { label: "Stage" },
          { label: "Sync" },
          { label: "Last sync", align: "right" },
        ]}
        gridTemplate={CASE_GRID}
        gap={CASE_GAP}
        items={data?.items}
        loadingHeight={64}
        emptyTitle="No cases match this filter."
        renderRow={(c, _i, isLast) => {
          const tone = SYNC_TONE[c.sync_status] || SYNC_TONE.synced;
          return (
            <ListRow
              key={c.case_id}
              as={Link}
              to={`/city/cases/${c.case_id}`}
              gridTemplate={CASE_GRID}
              gap={CASE_GAP}
              isLast={isLast}
              data-testid={`city-cases-row-${c.case_id}`}
            >
              <div className="min-w-0">
                <div
                  className="tabular-nums"
                  style={{
                    color: "#3A5563",
                    fontWeight: 700,
                    fontSize: 12,
                    letterSpacing: "0.02em",
                  }}
                >
                  {c.case_id}
                </div>
                <div
                  style={{
                    fontFamily: '"Fraunces", Georgia, serif',
                    fontWeight: 600,
                    fontSize: 15,
                    color: "#1B1F4B",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {c.parcel?.address || "—"}
                </div>
                <div
                  style={{
                    fontFamily: '"Newsreader", Georgia, serif',
                    fontStyle: "italic",
                    color: "#6C6B85",
                    fontSize: 11.5,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {c.parcel?.neighborhood || "—"} · {c.design?.name || "—"}
                </div>
              </div>
              <div style={{ justifySelf: "start" }}>
                <StagePill stage={c.current_stage} />
              </div>
              <span
                style={{
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: tone.bg,
                  color: tone.fg,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  whiteSpace: "nowrap",
                  justifySelf: "start",
                }}
              >
                {tone.label}
              </span>
              <span
                className="tabular-nums"
                style={{
                  fontFamily: '"Newsreader", Georgia, serif',
                  fontStyle: "italic",
                  color: "#6C6B85",
                  fontSize: 12,
                  textAlign: "right",
                  whiteSpace: "nowrap",
                }}
              >
                {relativeDate(c.last_sync_at)}
              </span>
            </ListRow>
          );
        }}
      />
    </section>
  );
}
